/**
 * Numeric primitives.
 *
 * In a billing product the numbers *are* the interface, so how they are set is
 * a product decision rather than a formatting detail. Three kinds of number
 * appear on a till and they want different treatment:
 *
 *   Money  an amount of rupees — scanned down a column, compared, verified
 *   Qty    a count with a unit — read, not compared
 *   Code   a SKU, barcode, invoice number, GSTIN — transcribed character by
 *          character, where confusing 0 for O is an actual error
 *
 * Shared rules:
 *   • Tabular figures, always. Set globally in theme.css so a digit can never
 *     be proportional by omission — this used to be opted into by hand at 313
 *     call sites, which is a guarantee only where someone remembered.
 *   • Money is right-aligned. A column of amounts is read by its decimal point.
 */
import { cn } from '@/utils/cn';

/** Indian grouping, 2dp, no currency symbol — the symbol is rendered separately. */
const DIGITS = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export type MoneySize = 'inherit' | 'total' | 'total-sm';

export function Money({
  value,
  size = 'inherit',
  bare = false,
  signed = false,
  className,
}: {
  value: number | null | undefined;
  /** `total` (24px) for a grand total, `total-sm` (18px) for a subtotal. */
  size?: MoneySize;
  /** Drop the ₹ — for a column whose header already says the unit. */
  bare?: boolean;
  /** Show a + on positive values. For ledger movements, not for prices. */
  signed?: boolean;
  className?: string;
}) {
  // An absent value is not zero, and a till should not claim it is. A dash
  // reads as "nothing here"; ₹0.00 reads as "we checked, it is zero".
  if (value == null || Number.isNaN(value)) {
    return (
      <span className={cn('tabular-nums text-fg-tertiary', className)} aria-label="no value">
        —
      </span>
    );
  }

  const negative = value < 0;
  const [whole, paise] = DIGITS.format(Math.abs(value)).split('.');

  const sizeClass =
    size === 'total' ? 'text-total' : size === 'total-sm' ? 'text-total-sm' : undefined;

  return (
    <span
      className={cn(
        'whitespace-nowrap tabular-nums',
        sizeClass,
        // Red for money going the wrong way. The brand assigns Red to overdue
        // and refunds, and a minus sign alone is too small a mark to carry that
        // meaning at a glance across a table.
        negative && 'text-danger-ink',
        className,
      )}
    >
      {/* U+2212 MINUS, not a hyphen: same width as the digits, so a negative
          row still aligns with the positive ones above it. */}
      {negative ? '−' : signed ? '+' : null}
      {/* The symbol and the paise are stepped down in SIZE only — they keep
          whatever colour the amount inherits.
          An earlier version dimmed them with opacity, which is the obvious way
          to say "secondary" and the wrong one: measured against the real
          background it put the ₹ at 2.13:1 and the paise at 2.72:1, well under
          AA. Opacity cannot be used for de-emphasis on anything that still has
          to be read, and on a bill every character still has to be read. Size
          alone carries the hierarchy and cannot affect contrast. */}
      {!bare && <span className="text-[0.82em]">₹</span>}
      {whole}
      <span className="text-[0.82em]">.{paise}</span>
    </span>
  );
}

/**
 * A quantity with its unit. The unit is secondary — "12" is the answer, "PCS"
 * is the footnote — and is never allowed to wrap away from its number.
 */
export function Qty({
  value,
  unit,
  className,
}: {
  value: number | null | undefined;
  unit?: string | null;
  className?: string;
}) {
  if (value == null || Number.isNaN(value)) {
    return <span className={cn('tabular-nums text-fg-tertiary', className)}>—</span>;
  }
  // Counts are usually whole; only show decimals when the unit is divisible.
  const text = Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, '');
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)}>
      {text}
      {/* Size and letter-spacing, not opacity — see the note in Money. */}
      {unit ? (
        <span className="ml-1 text-[0.78em] font-medium uppercase tracking-[0.06em]">{unit}</span>
      ) : null}
    </span>
  );
}

/**
 * A code that gets read aloud, typed into a portal, or checked against a label
 * — SKU, barcode, invoice number, GSTIN, HSN.
 *
 * Slashed zero is switched on here and nowhere else. It is the one place the
 * 0/O distinction is load-bearing, and applying it to money as well would make
 * the whole till read as a terminal, which is not what this brand is.
 */
export function Code({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn('whitespace-nowrap tabular-nums tracking-[0.02em]', className)}
      style={{ fontFeatureSettings: "'zero' 1, 'ss03' 1" }}
    >
      {children}
    </span>
  );
}
